/**
 * The sheet element's row viewport: the window of body rows a column pass
 * binds against, the focused row it keeps pinned, and the residency the
 * rendered emit follows. It owns its state (the focused row, the membership
 * key and the residency it was computed against); the element and its bind
 * step reach it through these operations only. Moved out of
 * sheet-element.ts unchanged in behaviour.
 */
import { platformOf } from "@tento-lona/host";
import { type Option, RowLocalId } from "@tento-lona/sheets";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { nonnull } from "@tento-core/assert";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import type { Size } from "@tento-ui/layout-utils";
import { RowViewport } from "@tento-lona/sheets/viewport";
import { type RowResidency, RowResidencyOwner } from "./binder/row-residency.ts";
import type { EmitResidency } from "./sheet-emit-handle.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";

/** The geometry a row membership was computed against: a change resets it. */
type ResidencyKey = {
  readonly sheet: SheetViewModel.Surface<SheetBinder>;
  readonly layout: SheetViewModel.Surface<SheetBinder>["layout"];
  readonly rows: readonly SheetRow.Joined.Handle[];
};

/** The row viewport of one sheet element (see the module note). */
export class SheetRowViewport {
  readonly #host: SheetRowViewport.Host;
  #residencyKey: Option<ResidencyKey> = null;
  #focusedSerial: Option<string> = null;
  #focusedWire: Option<string> = null;
  #membership = "";
  #queued = false;
  #epoch = 0;

  constructor(host: SheetRowViewport.Host) {
    this.#host = host;
  }

  /** The row viewport a column pass binds against, at `top` (else the current scroll position). */
  input(top: Option<number> = null): RowViewport.Input {
    const sheet = this.#host.sheet;
    const scrollTop = top ?? this.#host.scrollTop();
    const size = this.#host.size;
    if (sheet == null) {
      // Unbound: the previous sheet's residency and membership are stale, and
      // `refresh` must not select against them.
      this.#residencyKey = null;
      this.#membership = "";
      return { mode: "all" };
    }
    if (scrollTop == null || size == null) return { mode: "all" };
    const rows = sheet.joinedTree.flattened.rows;
    if (this.#residencyKey?.sheet !== sheet || this.#residencyKey.layout !== sheet.layout || this.#residencyKey.rows !== rows) {
      this.#membership = "";
      this.#residencyKey = { sheet, layout: sheet.layout, rows };
    }
    const pinned: RowLocalId[] = [];
    const residency = this.#host.residencyOwner.of(sheet);
    const focused = this.#focusedSerial == null ? null : residency.rowsBySerial.get(this.#focusedSerial);
    const focusedByWire = this.#focusedWire == null ? null : residency.rowForWire(this.#focusedWire);
    if (focused != null || focusedByWire != null) pinned.push(nonnull(focused ?? focusedByWire, "focused row").id);
    const sticky = SheetElementLayout.headerHeight(sheet);
    return {
      mode: "window",
      topPx: Math.max(0, scrollTop + sticky - SheetElementLayout.bodyMarginTop(sheet)),
      heightPx: Math.max(0, size.height - sticky - sheet.layout.footerRowsHeightPx),
      overscanBeforePx: 240,
      overscanAfterPx: 240,
      pinned,
    };
  }

  /**
   * Recompute the membership at `top`; when it changed, queue one
   * membership pass (`Host.membershipChanged`) for the next microtask.
   */
  refresh(top: Option<number> = null): void {
    const input = this.input(top);
    const residency = this.#resolved();
    if (residency == null) return;
    const membership = residency.select(input).selection.rows.map(({ row }) => row.rowId.serial).join(",");
    if (membership === this.#membership) return;
    this.#membership = membership;
    if (this.#queued) return;
    this.#queued = true;
    const epoch = this.#epoch;
    queueMicrotask(() => {
      if (epoch !== this.#epoch) return;
      this.#queued = false;
      this.#host.membershipChanged();
    });
  }

  /**
   * The residency the rendered emit's per-row pass follows: the rows the
   * current row viewport selects, over the SDK sheet reads the load demand
   * walks; `null` when the viewport is not windowed (every row).
   */
  emitResidency(): Option<EmitResidency> {
    const sheet = this.#host.sheet;
    const input = this.input();
    const residency = this.#resolved();
    if (sheet == null || input.mode === "all" || residency == null) return null;
    return {
      reads: platformOf(sheet.handle).sheet,
      resident: residency.select(input).selection.rows.map(({ row }) => row.rowId),
    };
  }

  /** Pin the row of a focused cell. */
  focus(cell: SheetCell.CellId): void {
    this.#focusedWire = String(cell.row);
    this.input();
    this.#focusedSerial = this.#resolved()?.rowForWire(this.#focusedWire)?.id.serial ?? null;
    this.refresh();
  }

  /** Unpin the row of a blurred cell, when it is the focused one. */
  blur(cell: SheetCell.CellId): void {
    const focused = this.#focusedSerial == null ? null : this.#resolved()?.rowsBySerial.get(this.#focusedSerial);
    if (String(cell.row) === this.#focusedWire || String(cell.row) === focused?.id.toString()) {
      this.#forgetFocus();
      this.refresh();
    }
  }

  /**
   * A bind to another view model: drop the membership and its residency,
   * cancel a queued pass, and drop the focused row too when `dropFocus` (the
   * new view model reads another sheet handle).
   */
  resetForContext(dropFocus: boolean): void {
    this.#epoch++;
    this.#queued = false;
    this.#membership = "";
    this.#residencyKey = null;
    if (dropFocus) this.#forgetFocus();
  }

  /** Drop the focused row (a bind to another sheet). */
  forgetFocus(): void {
    this.#forgetFocus();
  }

  /** Recycle or disconnect: everything resets, and a queued pass is dropped. */
  reset(): void {
    this.resetForContext(true);
  }

  #forgetFocus(): void {
    this.#focusedWire = null;
    this.#focusedSerial = null;
  }

  /** The residency the last row viewport was computed against, if any. */
  #resolved(): Option<RowResidency> {
    const key = this.#residencyKey;
    return key == null ? null : this.#host.residencyOwner.of(key.sheet);
  }
}

/** Types that go with `SheetRowViewport`: `Host`. */
export namespace SheetRowViewport {
  /** What the row viewport reads of its element, and the pass it asks for. */
  export interface Host {
    /** The bound view model, if any. */
    readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
    /** The element's measured size. */
    readonly size: Option<Size>;
    /** The residency the element's viewport and default binder share. */
    readonly residencyOwner: RowResidencyOwner;
    /** The current vertical scroll position, if measured. */
    scrollTop(): Option<number>;
    /** The membership changed: run the membership pass (a no-op once the element let go of its sheet). */
    membershipChanged(): void;
  }
}
